import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api/client'
import { useSavedMessages } from '@/api/queries'
import { Button, Spinner, useToast } from '@/components/ui'
import { Markdown } from '@/components/Markdown'
import styles from './ActivityPage.module.css'

export function SavedPage({ embedded = false }: { embedded?: boolean }): ReactElement {
  const saved = useSavedMessages()
  const toast = useToast()
  const remove = async (id: string): Promise<void> => {
    try {
      await api.del(`/messages/${encodeURIComponent(id)}/saved`)
      await saved.refetch()
    } catch { toast.error('The saved message could not be removed. Try again.') }
  }
  return <section className={styles.page} aria-label="Saved for later">
    {!embedded ? <header className={styles.header}><h1>Saved for later</h1></header> : null}
    <div className={styles.body}>
      {embedded ? <div className={styles.savedIntro}><h2>Saved messages</h2><p>Only you can see this list.</p></div> : null}
      {saved.isPending ? <Spinner label="Loading saved messages" /> : null}
      {saved.isError ? <div role="alert"><p>Saved messages could not be loaded.</p><Button onClick={() => void saved.refetch()}>Retry</Button></div> : null}
      {saved.data?.length === 0 ? <p>Save a message from its menu to find it here later.</p> : null}
      <ul className={styles.list}>{saved.data?.map(({ message, room }) => <li key={message.id} className={styles.savedItem}>
        <Link to={`/rooms/${encodeURIComponent(room.id)}?${new URLSearchParams({ message: message.id, ...(message.parent_id ? { thread: message.parent_id } : {}) }).toString()}`}>
          {room.name ?? room.slug ?? 'Conversation'} · {message.sender.display_name || message.sender.username}
        </Link>
        <Markdown text={message.text || (message.attachments.length > 0 ? 'Shared a file' : 'Message')} />
        <Button variant="ghost" onClick={() => void remove(message.id)}>Remove from saved</Button>
      </li>)}</ul>
      {saved.hasNextPage ? <Button loading={saved.isFetchingNextPage} onClick={() => void saved.fetchNextPage()}>Load more saved messages</Button> : null}
    </div>
  </section>
}
